/**
 * 送信予約の「同日置き換え」確認フロー。
 *
 * 送信予約日時が入力されているときだけ scheduled_conflicts を引き、同じ日（JST）に
 * 既存の予約がある対象がいれば確認モーダルを挟む。0件なら従来どおりそのまま送信する。
 */
import dayjs from "dayjs";
import { enqueueSnackbar } from "notistack";

export type ScheduledConflictDecision = { kind: "send" } | { kind: "confirm"; conflictCount: number };

export const buildInvestmentReplaceConfirmText = (conflictCount: number) =>
  `同じ日にすでに送信予約があるユーザーが${conflictCount}名います。\n既存の予約を取り消して、今回の予約に置き換えますか？`;

export const buildAssessmentReplaceConfirmText = (conflictCount: number) =>
  `同じ日にすでに送信予約がある物件が${conflictCount}件あります。\n既存の予約を取り消して、今回の予約に置き換えますか？`;

/**
 * 送信前に確認モーダルが必要かを判定する。
 *
 * - 送信予約日時が未入力（＝即時送信）なら問い合わせない
 * - 送信予約日時が現在以前なら問い合わせない（サーバー側が即時送信として扱い replace_same_day を
 *   無視するため、置き換えモーダルを出すと「既存の予約を取り消す」という嘘の説明になる）
 * - 対象が0件なら問い合わせない（対象がサーバー側で確定する経路は resolvesTargetOnServer を立てる）
 * - 問い合わせに失敗した場合は送信を止めない（置き換え確認は補助的な導線のため、
 *   ここで送信をブロックすると通常の予約すら出せなくなる）。ただし黙って素通ししないよう、
 *   既存の予約が残る可能性を通知する
 */
export const resolveScheduledConflict = async (params: {
  proposalDate?: string;
  targetIds: number[];
  /**
   * 対象が手元の ID ではなくサーバー側で確定する経路（絞り込み条件での一括提案）かどうか。
   * true のときは targetIds が空でも問い合わせる（fetchConflictCount 側が条件を渡して数える）。
   */
  resolvesTargetOnServer?: boolean;
  fetchConflictCount: (targetIds: number[], proposalDate: string) => Promise<number>;
}): Promise<ScheduledConflictDecision> => {
  const { proposalDate, targetIds, resolvesTargetOnServer = false, fetchConflictCount } = params;
  if (!proposalDate) return { kind: "send" };
  if (targetIds.length === 0 && !resolvesTargetOnServer) return { kind: "send" };
  // 過去/不正/現在時刻ちょうどは端末時計基準で判定する。サーバー時計とずれた境界ケースでは
  // 確認をスキップして送信に倒れるが、その場合も重複はサーバーの日次上限で failed として可視化される
  const parsedProposalDate = dayjs(proposalDate);
  if (!parsedProposalDate.isValid() || !parsedProposalDate.isAfter(dayjs())) return { kind: "send" };

  try {
    const conflictCount = await fetchConflictCount(targetIds, proposalDate);
    if (conflictCount > 0) return { kind: "confirm", conflictCount };
    return { kind: "send" };
  } catch (error) {
    console.error(error);
    enqueueSnackbar(
      "送信予約の重複確認ができませんでした。同じ日にすでに予約がある場合、既存の予約は取り消されずに残ります。",
      { variant: "warning" }
    );
    return { kind: "send" };
  }
};
